import { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { useSettingsStore } from '@/store/settingsStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

export function WhatsAppSettings() {
  const { 
    settings, 
    isLoading, 
    error, 
    updateSettings, 
    fetchSettings, 
    hasInitialFetch 
  } = useSettingsStore();
  
  const [number, setNumber] = useState(settings.whatsappNumber);
  const [message, setMessage] = useState(settings.whatsappMessage);
  const [validationError, setValidationError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Fetch settings when component mounts
  useEffect(() => {
    if (!hasInitialFetch) {
      fetchSettings();
    }
  }, [hasInitialFetch, fetchSettings]);

  // Update local state when settings change
  useEffect(() => {
    setNumber(settings.whatsappNumber);
    setMessage(settings.whatsappMessage);
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const phoneValidation = validateWhatsAppPhoneNumber(number);
    if (phoneValidation !== true) {
      setValidationError(phoneValidation);
      return;
    }

    try {
      setIsSaving(true);
      setValidationError('');
      await updateSettings({
        whatsappNumber: number,
        whatsappMessage: message
      });
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !hasInitialFetch) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="h-5 w-5 text-[#25D366]" />
        <h3 className="text-lg font-semibold">إعدادات واتساب</h3>
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatsappNumber">رقم الواتساب</Label>
        <Input
          id="whatsappNumber"
          value={number}
          onChange={(e) => {
            setNumber(e.target.value);
            setValidationError('');
          }}
          placeholder="009665xxxxxxxx"
          dir="ltr"
          className="text-left"
          disabled={isSaving}
        />
        {validationError && <p className="text-sm text-red-500 whitespace-pre-line">{validationError}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatsappMessage">الرسالة الافتراضية</Label>
        <Input
          id="whatsappMessage"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="أدخل الرسالة الافتراضية"
          disabled={isSaving}
        />
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      <Button 
        type="submit" 
        className="bg-[#25D366] hover:bg-[#128C7E]"
        disabled={isSaving}
      >
        {isSaving ? (
          <>
            <LoadingSpinner className="ml-2" />
            جاري الحفظ...
          </>
        ) : (
          <>
            <MessageCircle className="h-4 w-4 ml-2" />
            حفظ الإعدادات
          </>
        )}
      </Button>
    </form>
  );
}




function validateWhatsAppPhoneNumber(phone: string): true | string {
  if (!phone?.trim()) {
    return 'رقم الهاتف مطلوب';
  }

  // Kuwait and Saudi phone number patterns
  const saudiPattern = /^(9665)\d{8}$/;  // Saudi: 96650xxxxxxx (8 digits after prefix)
  const kuwaitPattern = /^(965[569])\d{7}$/; // Kuwait: 965[569]xxxxxxx

  if (!saudiPattern.test(phone) && !kuwaitPattern.test(phone)) {
    return 'رقم الهاتف غير صحيح. يجب أن يكون بالصيغة الدولية:\n- السعودية: 9665xxxxxxx\n- 965الكويت: [569]xxxxxxx';
  }

  return true;
}